/**
 * The all-projects board's data — V6-03 §4.
 *
 * One endpoint serves every column; moves go through the same
 * `PATCH /tasks/:id/move` the project board uses, so a status change made here
 * fires the same events, emails and chat messages as one made there.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AllProjectsBoardFilters,
  AllProjectsBoardResult,
  StatusCategory,
} from '@virtuetasks/types';
import { api, qs } from '@/lib/api';
import type { TaskView } from './api';

export const ALL_PROJECTS_BOARD_KEY = 'all-projects-board';

/** Cursors are per column, so the query carries `category:token` pairs. */
export type ColumnCursors = Partial<Record<StatusCategory, string>>;

function cursorParam(cursors: ColumnCursors): string | undefined {
  const pairs = Object.entries(cursors)
    .filter(([, token]) => Boolean(token))
    .map(([category, token]) => `${category}:${token}`);
  return pairs.length ? pairs.join(',') : undefined;
}

export function toBoardQuery(
  filters: AllProjectsBoardFilters,
  cursors: ColumnCursors,
): Record<string, unknown> {
  return {
    projectId: filters.projectId?.length ? filters.projectId.join(',') : undefined,
    assigneeId: filters.assigneeId?.length ? filters.assigneeId.join(',') : undefined,
    priority: filters.priority?.length ? filters.priority.join(',') : undefined,
    labelId: filters.labelId?.length ? filters.labelId.join(',') : undefined,
    dueBucket: filters.dueBucket,
    justMine: filters.justMine ? 'true' : undefined,
    cursor: cursorParam(cursors),
  };
}

export function useAllProjectsBoard(filters: AllProjectsBoardFilters, cursors: ColumnCursors) {
  return useQuery({
    queryKey: [ALL_PROJECTS_BOARD_KEY, filters, cursors],
    queryFn: async () =>
      (
        await api.get<AllProjectsBoardResult>(
          `/tasks/all-projects-board${qs(toBoardQuery(filters, cursors))}`,
        )
      ).data,
    // A board that flashes an empty skeleton every time a filter changes reads
    // as broken; keeping the previous columns while the next page loads is the
    // difference between "filtering" and "reloading".
    placeholderData: (previous) => previous,
  });
}

export function useBoardFilters() {
  return useQuery({
    queryKey: [ALL_PROJECTS_BOARD_KEY, 'filters'],
    queryFn: async () =>
      (await api.get<AllProjectsBoardFilters>('/tasks/all-projects-board/filters')).data,
  });
}

export function useSaveBoardFilters() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (filters: AllProjectsBoardFilters) =>
      (await api.put<AllProjectsBoardFilters>('/tasks/all-projects-board/filters', filters)).data,
    onSuccess: (filters) =>
      qc.setQueryData([ALL_PROJECTS_BOARD_KEY, 'filters'], filters),
  });
}

/** The same move endpoint the project board uses — V6-03 §4, deliberately. */
export function useMoveOnAllProjectsBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, statusId }: { id: string; statusId: string }) =>
      (await api.patch<TaskView>(`/tasks/${id}/move`, { statusId })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [ALL_PROJECTS_BOARD_KEY] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
